import { braintreeTracking, registrationDataTrafficTracker } from '../services/diiService.js';
import { safeStringify } from './utils.js';

function isTokenizeError(value) {
    return (value &&
        value instanceof Object &&
        // eslint-disable-next-line no-prototype-builtins
        value.hasOwnProperty('code') &&
        typeof value.code === 'string');
}
function getInvalidFieldKeys(tokenizeError) {
    const maybeInvalidFieldKeys = tokenizeError.details?.invalidFieldKeys;
    return maybeInvalidFieldKeys && Array.isArray(maybeInvalidFieldKeys) ? maybeInvalidFieldKeys : [];
}
function getMessage(tokenizeError) {
    return tokenizeError.message && typeof tokenizeError.message === 'string'
        ? tokenizeError.message
        : '';
}
function maybeBraintreeTracking(containerType, eventType) {
    if (containerType === 'registration' || containerType === 'default') {
        braintreeTracking(eventType);
    }
}
function maybeTrafficNotificationError(field, value, isSupported) {
    if (field === 'number' && !isSupported) {
        return 'Unsupported card number';
    }
    else if (!value.isValid) {
        return 'Invalid value';
    }
    else if (value.isEmpty) {
        return 'Empty value';
    }
    else {
        return null;
    }
}
const FIELD_NAMES = ['number', 'cardholderName', 'expirationMonth', 'expirationYear', 'cvv'];
function isFieldName(value) {
    return FIELD_NAMES.indexOf(value) !== -1;
}
function getDiiEventName(containerType) {
    switch (containerType) {
        case 'registration':
            return 'checkout_registerBT';
        case 'myaccount':
            return undefined;
        default:
            return 'edit_paymentBT';
    }
}
function maybeTrackErrors(containerType, trafficNotifications) {
    const maybeDiiEventName = getDiiEventName(containerType);
    if (maybeDiiEventName) {
        const { setTrackingErrors } = registrationDataTrafficTracker();
        setTrackingErrors(maybeDiiEventName, trafficNotifications);
    }
}
function makeTrafficNotifications(tokenizeError) {
    let trafficNotifications;
    if (isTokenizeError(tokenizeError)) {
        switch (tokenizeError.code) {
            case 'HOSTED_FIELDS_FIELDS_EMPTY':
                // All fields are empty
                trafficNotifications = FIELD_NAMES.map((field) => {
                    return { type: 'frontError', field, error: 'Empty value' };
                });
                break;
            case 'HOSTED_FIELDS_FIELDS_INVALID':
                // Some fields are invalid
                trafficNotifications = getInvalidFieldKeys(tokenizeError).map((field) => {
                    return { type: 'frontError', field, error: 'Invalid value' };
                });
                break;
            case 'HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE':
                // occurs when:
                //   * the client token used for client authorization was generated
                //     with a customer ID and the fail on duplicate payment method
                //     option is set to true
                //   * the card being tokenized has previously been vaulted (with any customer)
                // See: https://developer.paypal.com/braintree/docs/reference/request/client-token/generate#options.fail_on_duplicate_payment_method
                trafficNotifications = [
                    {
                        type: 'frontError',
                        field: '',
                        error: "The payment method already exists in the customer's vault",
                    },
                ];
                break;
            case 'HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED':
                // occurs when:
                //   * the client token used for client authorization was generated
                //     with a customer ID and the verify card option is set to true
                //     and you have credit card verification turned on in the Braintree
                //     control panel
                //   * the cvv does not pass verification (https://developer.paypal.com/braintree/docs/reference/general/testing#avs-and-cvv/cid-responses)
                // See: https://developer.paypal.com/braintree/docs/reference/request/client-token/generate#options.verify_card
                trafficNotifications = [
                    {
                        type: 'frontError',
                        field: '',
                        error: 'CVV did not pass verification',
                    },
                ];
                break;
            case 'HOSTED_FIELDS_FAILED_TOKENIZATION':
                // occurs for any other tokenization error on the server
                trafficNotifications = [
                    {
                        type: 'frontError',
                        field: '',
                        error: `Tokenization failed server side with error: ${getMessage(tokenizeError)}`,
                    },
                ];
                break;
            case 'HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR':
                // occurs when the Braintree gateway cannot be contacted
                trafficNotifications = [
                    {
                        type: 'frontError',
                        field: '',
                        error: `Network error occurred when tokenizing with error: ${getMessage(tokenizeError)}`,
                    },
                ];
                break;
            default:
                trafficNotifications = [
                    {
                        type: 'frontError',
                        field: '',
                        error: `Something bad happened when tokenizing, error: ${getMessage(tokenizeError)}`,
                    },
                ];
        }
    }
    else {
        trafficNotifications = [
            {
                type: 'frontError',
                field: '',
                error: `Something bad happened when tokenizing, general error: ${safeStringify(tokenizeError)}`,
            },
        ];
    }
    return trafficNotifications;
}
function gatherDataByFieldName(state, isSupported) {
    const validityByFieldName = {}, emptinessByFieldName = {}, trafficNotifications = [];
    Object.entries(state.fields).forEach(([field, value]) => {
        if (isFieldName(field)) {
            const error = maybeTrafficNotificationError(field, value, isSupported);
            if (error) {
                trafficNotifications.push({ type: 'frontError', field, error });
            }
            validityByFieldName[field] = value.isValid;
            emptinessByFieldName[field] = value.isEmpty;
        }
    });
    return { validityByFieldName, emptinessByFieldName, trafficNotifications };
}

export { gatherDataByFieldName, getDiiEventName, getInvalidFieldKeys, getMessage, isFieldName, isTokenizeError, makeTrafficNotifications, maybeBraintreeTracking, maybeTrackErrors, maybeTrafficNotificationError };
//# sourceMappingURL=braintreeFunctions.js.map
